import { Flex, Box, useColorMode, useToken } from "@chakra-ui/react";
import {
  hairColors,
  hairs,
  eyes,
  noses,
  skinColors
} from "src/modules/avatar/baby-customization";
import { Lottie } from "src/components/Lottie";
import { BodyText2 } from "src/atoms/typography";
import Field from "../Field";
import { ReactComponent as NoneIcon } from "../../icons/none.svg";

import eyes0Awake from "./animations/Eyes0-awake.json";
import eyes1Awake from "./animations/Eyes1-awake.json";
import eyes2Awake from "./animations/Eyes2-awake.json";
import eyes0Sleeping from "./animations/Eyes0-sleeping.json";
import eyes1Sleeping from "./animations/Eyes1-sleeping.json";
import eyes2Sleeping from "./animations/Eyes2-sleeping.json";

import hair0 from "./animations/Hair0.json";
import hair1 from "./animations/Hair1.json";
import hair2 from "./animations/Hair2.json";
import hair3 from "./animations/Hair3.json";
import hair4 from "./animations/Hair4.json";

import nose0 from "./animations/Nose0.json";
import nose1 from "./animations/Nose1.json";
import nose2 from "./animations/Nose2.json";

const animations = {
  hairs: [hair0, hair1, hair2, hair3, hair4],
  eyes: {
    awake: [eyes0Awake, eyes1Awake, eyes2Awake],
    sleeping: [eyes0Sleeping, eyes1Sleeping, eyes2Sleeping]
  },
  noses: [nose0, nose1, nose2]
};

export default function Face({
  isSleeping = true,
  avatar,
  handleAvatarStyleUpdate,
  t
}) {
  const { colorMode } = useColorMode();
  const surfaceElevated1 = useToken("colors", "surfaceElevated1");
  const primary = useToken("colors", "primary");
  const isDarkMode = colorMode === "dark";

  const { avatarCustomization, moreDataJson } = avatar;
  const { hair, nose, eye, hairColor, skinColor } = avatarCustomization;
  const additionalColor =
    skinColors[skinColor || moreDataJson?.skinColor].AdditionalSkin;
  const disableColors = hair === "NO_HAIR";

  const eyesLayers = Object.values(eyes).map((val) => val[0]);
  const hairsLayers = Object.values(hairs).map((val) => val[0]);
  const skinBasedCss = Object.values({ ...eyes, ...noses, ...hairs }).reduce(
    (result, layers) => {
      layers.forEach((layer) => {
        if (layer.includes("Base")) {
          result =
            result +
            `
            & .${layer} {
              ${
                (eyesLayers.includes(layer) && !isSleeping) ||
                hairsLayers.includes(layer)
                  ? "fill"
                  : "stroke"
              }:${
              hairsLayers.includes(layer)
                ? hairColor
                : isDarkMode
                ? "#885031"
                : additionalColor
            };
            }
            `;
        }
      });

      return result;
    },
    ""
  );

  return (
    <Box
      py="16px"
      overflow="auto"
      height="100%"
      maxHeight="317px"
      css={`
        ${skinBasedCss}
      `}
    >
      <BodyText2 color="highEmphasis" mb="8px">
        {t("avatar.hairType")}
      </BodyText2>
      <Flex mb="16px" gap="14px" flexWrap="wrap">
        <Field
          onClick={() => handleAvatarStyleUpdate({ hair: "NO_HAIR" })}
          isSelected={hair === `NO_HAIR`}
        >
          <Box sx={{ svg: { fill: "rgba(0, 0, 0, 0.54)" } }}>
            <NoneIcon height="20px" width="20px" />
          </Box>
        </Field>
        {animations.hairs.map((animation, i) => {
          return (
            <Field
              key={i}
              onClick={() => handleAvatarStyleUpdate({ hair: `HAIR_${i}` })}
              isSelected={hair === `HAIR_${i}`}
            >
              <Lottie
                width="42px"
                height="42px"
                animationData={animation}
                browserProps={{ hasRawData: true }}
              />
            </Field>
          );
        })}
      </Flex>
      <Box
        transition="all 0.3s"
        minHeight={disableColors ? "0px" : "60px"}
        maxHeight={disableColors ? "0px" : "100%"}
        opacity={disableColors ? 0 : 1}
        zIndex={disableColors ? -10 : 0}
      >
        <BodyText2 color="highEmphasis" mb="8px">
          {t("avatar.hairColor")}
        </BodyText2>
        <Flex ml="6px" mb="16px" gap="12px">
          {hairColors.map((color) => {
            const outline = color === "#000000";
            return (
              <Box
                onClick={() => handleAvatarStyleUpdate({ hairColor: color })}
                key={color}
                bg={color}
                width="32px"
                height="32px"
                borderRadius="100%"
                {...(outline && {
                  boxShadow: "0 0 0 1pt rgba(255, 255, 255, 0.3)"
                })}
                {...(color === hairColor && {
                  boxShadow: `0 0 0 2pt ${surfaceElevated1},0 0 0 4pt ${primary}`
                })}
              />
            );
          })}
        </Flex>
      </Box>
      <BodyText2 color="highEmphasis" mb="8px">
        {t("avatar.eyesType")}
      </BodyText2>
      <Flex mb="16px" gap="14px">
        {animations.eyes[isSleeping ? "sleeping" : "awake"].map(
          (animation, i) => {
            return (
              <Field
                key={i}
                onClick={() => handleAvatarStyleUpdate({ eye: `EYE_${i}` })}
                isSelected={eye === `EYE_${i}`}
              >
                <Lottie
                  width="42px"
                  height="42px"
                  animationData={animation}
                  browserProps={{ hasRawData: true }}
                />
              </Field>
            );
          }
        )}
      </Flex>
      <BodyText2 color="highEmphasis" mb="8px">
        {t("avatar.noseType")}
      </BodyText2>
      <Flex gap="14px">
        {animations.noses.map((animation, i) => {
          return (
            <Field
              key={i}
              onClick={() => handleAvatarStyleUpdate({ nose: `NOSE_${i}` })}
              isSelected={nose === `NOSE_${i}`}
            >
              <Lottie
                width="42px"
                height="42px"
                animationData={animation}
                browserProps={{ hasRawData: true }}
              />
            </Field>
          );
        })}
      </Flex>
    </Box>
  );
}
